import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  CountAndSort,
  PageDescription,
  PageHeader,
  PageTitle,
  ResultsCount,
  Sort,
  CountNumber,
  VehicleListWrapper,
  VehiclesListContainer,
  Titles,
  ShowFiltersAndSort,
  CountAndSortWrapper,
  VehicleListElement,
} from './VehicleList.style';
import FilterTags from '../FilterTags';
import { LocationSearchData } from '../../types';
import VehicleCard from '../VehicleCard';
import SkeletonLoader from '../Skeleton';
import NoVehiclesFound from '../NoVehiclesFound';
import SortBy from '../SortBy';
import LocationFilter from '../Filters/LocationFilter/LocationFilter';
import { selectLocation } from '../../redux/locationReducer';
import { selectAppConfig } from '../../redux/appConfigReducer';
import LoadMore from '../LoadMore';
import useScrollTracker from '../../hooks/UI/useScrollTracker';
import AdditionalCtas from '../AdditionalCtas';
import useFilterTagProcessor from '../../hooks/Filters/useFilterTagProcessor';
import { selectFilters } from '../../redux/filtersReducer';
import FilterIcon from '../../assets/icons/FilterIcon';
import { COLORS } from '../../styles/variables';
import { selectVehiclesOneGraph } from '../../redux/vehiclesReducer';
import useFilterOptions from '../../hooks/Filters/useFilterOptions';
import { useGetOaddDealers } from '../../hooks/Fetch/useGetOaddDealers';
import { OADD_DEALERS_JSON } from '../../constants/environment';

interface VehicleListTypes {
  loading: boolean;
  isLaptop: boolean;
  isFiltersOpen: boolean;
  onLocationSearchOpen: () => void;
  setIsFiltersOpen: (arg: boolean) => void;
  fetchMore: () => void;
}

const VehicleList: React.FC<VehicleListTypes> = ({
  loading,
  isLaptop,
  isFiltersOpen,
  onLocationSearchOpen,
  setIsFiltersOpen,
  fetchMore,
}) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { location } = useSelector(selectLocation);
  const { marketType, isTier3page, isImageGalleryOn, listOfDealers, pageTitle, pageDescription } =
    useSelector(selectAppConfig);
  const { confirmedFilters } = useSelector(selectFilters);
  const { listOfAllFilters } = useFilterOptions();

  const { vehiclesList, totalCount } = useSelector(selectVehiclesOneGraph);

  const { filterCount } = useFilterTagProcessor(confirmedFilters, listOfAllFilters, isTier3page);

  const { t, i18n } = useTranslation();
  // const filteredVehicles = isTier3page
  //   ? vehiclesList
  //   : vehiclesList.filter((toCheck) => toCheck.modelPrice !== null);
  const filteredVehicles = vehiclesList;
  const shouldShowLoadMore =
    totalCount && vehiclesList !== null && vehiclesList.length < totalCount;

  const scrolled = useScrollTracker(236);

  const cardsToShow = 12;

  const mainDealer = listOfDealers?.find((dealer) => dealer.isMainDealer)?.dealerId;

  // Get Eligible OADD Dealers (dealers that have OADD webistes)
  const { eligibleOaddDealers } = useGetOaddDealers(OADD_DEALERS_JSON, isTier3page);

  const eligibleDealerIds = useMemo(() => {
    return eligibleOaddDealers.map((item) => item.dealerId);
  }, [eligibleOaddDealers]);

  const renderContent = useMemo(() => {
    if (loading && !isLoadingMore) {
      return (
        <VehiclesListContainer>
          {Array.from({ length: cardsToShow })
            .fill(null)
            .map((_, index) => (
              /* eslint-disable-next-line react/no-array-index-key */
              <SkeletonLoader key={index} />
            ))}
        </VehiclesListContainer>
      );
    }
    if (!loading && vehiclesList !== null && !vehiclesList?.length) {
      // hide CTAs for Tier3 Temporarily
      return <NoVehiclesFound noCtas={isTier3page} onLocationSearchOpen={onLocationSearchOpen} />;
    }
    return (
      <VehiclesListContainer scrolled={scrolled}>
        <VehicleListElement>
          {filteredVehicles?.map((vehicle) => (
            <VehicleCard
              vehicle={vehicle}
              key={`${vehicle.stockCar.id}.${vehicle.stockCar.dealer.id}`}
              isTier3page={isTier3page}
              marketType={marketType}
              isImageGalleryOn={isImageGalleryOn}
              mainDealer={mainDealer}
              eligibleOaddDealers={eligibleOaddDealers}
              eligibleDealerIds={eligibleDealerIds}
            />
          ))}
        </VehicleListElement>
      </VehiclesListContainer>
    );
  }, [
    eligibleDealerIds,
    eligibleOaddDealers,
    filteredVehicles,
    isImageGalleryOn,
    isLoadingMore,
    isTier3page,
    loading,
    mainDealer,
    marketType,
    onLocationSearchOpen,
    scrolled,
    vehiclesList,
  ]);

  const renderSortBy = useMemo(() => {
    if (isLaptop)
      return (
        <Sort>
          <SortBy />
        </Sort>
      );
    return (
      <ShowFiltersAndSort onClick={() => setIsFiltersOpen(true)}>
        <FilterIcon color={COLORS.black} />
        {t('vlp.vehicleResults.sortAndFilter')} ({filterCount})
      </ShowFiltersAndSort>
    );
  }, [filterCount, isLaptop, setIsFiltersOpen, t]);

  const renderTitle = useMemo(() => {
    if (isTier3page && pageTitle?.length) {
      return pageTitle;
    }
    // Tier 1
    return t('vlp.vehicleResults.pageTitle');
  }, [isTier3page, pageTitle, t]);

  const renderDescription = useMemo(() => {
    if (isTier3page && pageDescription?.length) {
      return pageDescription;
    }
    if (isTier3page && !pageDescription?.length) {
      return '';
    }
    // Tier 1
    return t('vlp.vehicleResults.pageDescription');
  }, [isTier3page, pageDescription, t]);

  return (
    <VehicleListWrapper isFiltersOpen={isFiltersOpen}>
      <PageHeader isFiltersOpen={isFiltersOpen}>
        {!isLaptop && !isTier3page && (
          <LocationFilter
            userSelectedLocation={location as LocationSearchData}
            market={marketType}
            isLaptop={isLaptop}
          />
        )}
        <Titles>
          <PageTitle data-testid="page-title">{renderTitle}</PageTitle>
          <PageDescription data-testid="page-description">{renderDescription}</PageDescription>
        </Titles>
        {isLaptop && <FilterTags />}
        <CountAndSortWrapper scrolled={scrolled}>
          <CountAndSort>
            <ResultsCount>
              <CountNumber data-testid="vehicle-count">{loading ? '##' : totalCount}</CountNumber>
              {t('vlp.vehicleResults.totalResults')}
            </ResultsCount>
            {renderSortBy}
          </CountAndSort>
          {!isLaptop && <FilterTags />}
        </CountAndSortWrapper>
      </PageHeader>
      {renderContent}
      {shouldShowLoadMore ? (
        <LoadMore
          fetchMore={fetchMore}
          isLoading={loading}
          isLoadingMore={isLoadingMore}
          setIsLoadingMore={setIsLoadingMore}
        />
      ) : null}
      {!isTier3page && <AdditionalCtas />}
    </VehicleListWrapper>
  );
};

export default VehicleList;
